import { useNavigate, useParams } from 'react-router-dom';
import {
  ESDM_DOMAINS,
  ESDM_ITEMS,
  esdmDomainKey,
  getEsdmDomainRollup,
  getItemsByLevelAndDomain,
} from '@aba-denver/core';
import { useData } from '../contexts/DataContext';

const LEVELS = [1, 2, 3, 4] as const;

export function EsdmReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getChildById, getChildEsdmProgress } = useData();

  const child = id ? getChildById(id) : undefined;

  if (!child) {
    return <div className="empty-state">Paciente não encontrado.</div>;
  }

  const progressDoc = getChildEsdmProgress(child.id);
  const rollup = getEsdmDomainRollup(ESDM_ITEMS, progressDoc?.items ?? {});

  const levelTotals = LEVELS.map((level) => {
    const rows = rollup.filter((r) => r.level === level);
    const acquired = rows.reduce((s, r) => s + r.acquired, 0);
    const total = rows.reduce((s, r) => s + r.total, 0);
    return { level, acquired, total, pct: total > 0 ? Math.round((acquired / total) * 100) : 0 };
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <a onClick={() => navigate(`/patients/${child.id}/esdm`)} style={{ cursor: 'pointer', fontSize: 13 }}>
            ‹ Checklist ESDM
          </a>
          <h1 style={{ marginTop: 6 }}>Resumo — {child.name}</h1>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>Aquisição por nível</h2>
        <div className="row" style={{ gap: 24, alignItems: 'flex-end' }}>
          {levelTotals.map(({ level, acquired, total, pct }) => (
            <div key={level} style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 13 }}>{pct}%</div>
              <div style={{ height: 90, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginTop: 4 }}>
                <div style={{ width: 34, height: `${Math.max(pct, 2)}%`, background: 'var(--primary)', borderRadius: 4 }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 12, marginTop: 4 }}>Nível {level}</div>
              <div className="muted" style={{ fontSize: 11 }}>{acquired}/{total}</div>
            </div>
          ))}
        </div>
      </div>

      {LEVELS.map((level) => {
        const levelRollup = rollup.filter((r) => r.level === level);
        if (levelRollup.length === 0) return null;
        return (
          <div key={level} className="card">
            <h2 style={{ marginBottom: 16 }}>Nível {level}</h2>
            <div className="stack" style={{ gap: 12 }}>
              {levelRollup.map((r) => {
                const domainName = ESDM_DOMAINS.find((d) => d.code === r.domain)?.name ?? r.domain;
                const pct = Math.round(r.pctAcquired * 100);
                const contributors = Array.from(
                  new Set(
                    getItemsByLevelAndDomain(r.level, r.domain)
                      .map((item) => progressDoc?.items[item.id]?.scoredByName)
                      .filter((name): name is string => !!name)
                  )
                );
                return (
                  <div key={`${r.level}-${r.domain}`}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <div style={{ fontWeight: 600 }}>{domainName}</div>
                      <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{pct}%</div>
                    </div>
                    <div className="progress-track" style={{ marginTop: 6 }}>
                      <div className="progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {r.acquired} adquiridos · {r.notAcquired} não adquiridos · {r.notStarted} não iniciados
                    </div>
                    {contributors.length > 0 && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 2, fontStyle: 'italic' }}>
                        Avaliado por: {contributors.join(', ')}
                      </div>
                    )}
                    {(() => {
                      const synthesis = progressDoc?.domainSynthesis?.[esdmDomainKey(r.level, r.domain)];
                      if (!synthesis?.text) return null;
                      return (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                          <div className="muted" style={{ fontSize: 11, fontWeight: 700 }}>
                            Interpretação clínica
                          </div>
                          <div style={{ fontSize: 13, marginTop: 2 }}>{synthesis.text}</div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
